import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material";
import React, { useState } from "react";
import { FormBox, OldPassBox, Span, TextButton, TextLabel } from "../../styles";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { ShowErrorToast, ShowSuccessToast } from "../../../Common/Toast/toast";
import useAuthentication from "../../../../Infrastructure/States/onAuthStateChange";
import ButtonLoading from "../../../Common/Button/ButtonLoading";
import { trackEvent } from "../../../../Infrastructure/Utilities/analytics";
import AnalyticsConstants from "../../../../Infrastructure/Constants/AnalyticsContants";
import ERROR_CONSTANTS from "../../../../Infrastructure/Constants/errorConstants";
import { useSelector } from "react-redux";
import { USER_LOGS_CLIENT } from "../../../../Infrastructure/Constants/logsConstant";
import { safeClientLogEvent } from "../../../../Infrastructure/Utilities/safeClientLogEvent";
import { StyledTextField } from "../../../../Infrastructure/Theme/styledComponents";
import passwordBoxSx from './styles';

export const PasswordBox = ({
  handleOpen,
  handleClose,
  showEditPass,
}) => {
  const { user, userId } = useAuthentication()
  const [loading, setLoading] = useState(false)
  const userData = useSelector((state) => state.userData.data);
  const [showOldPassword, setShowOldPassword] = useState(true);
  const [showNewPassword, setShowNewPassword] = useState(true);
  const [showReEnterPassword, setShowResetPassword] = useState(true);
  const passwordFormik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      reEnterPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string()
        .required("Old Password is required"),
      newPassword: Yup.string()
        .min(6, "Must be 6 characters")
        .test("special-characters", "Password must contain special characters", (value) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(value || ""))
        .required("New Password is required"),
      reEnterPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Confirm New Password is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true)
      const { newPassword, reEnterPassword, oldPassword } = values;
      try {
        const credential = EmailAuthProvider.credential(
          user.email,
          oldPassword
        );
        // Check if new passwords match
        if (newPassword !== reEnterPassword) {
          ShowErrorToast("New passwords don't match", {position: "top-center"})
          return;
        }
        await reauthenticateWithCredential(user, credential);
        // Update password
        await updatePassword(user, newPassword).then(async () => {
          ShowSuccessToast("Password updated successfully", {position: "top-center"})
          // Track event without blocking form submission
         try{
           trackEvent(AnalyticsConstants.PASSWORD_CHANGED, { user_id: userId })
        }catch(trackingError) {
          await safeClientLogEvent({
          ...USER_LOGS_CLIENT.TRACKING_EVENT_FAILED,
          uid: userId || null,
          source: "passwordBox.jsx",
          emailInfo: null,
          error: trackingError?.message || null
        })
        }
        })
        await safeClientLogEvent({
          ...USER_LOGS_CLIENT.ACCOUNT_PASSWORD_UPDATE_SUCCESS,
          uid: userId || null,
          source: "passwordBox.jsx",
          emailInfo: null,
          metadata: { 
            email: userData?.account?.email 
          } || null,
          error: null
        });
        handleClose();
        passwordFormik.resetForm();
      } catch (error) {
        if (error.code === "auth/wrong-password") {
          ShowErrorToast("Incorrect old password. Please check and try again.", {position: "top-center"})
        } else if (error.code === "auth/invalid-login-credentials") {
          ShowErrorToast("Incorrect old password. Please check and try again.", { position: "top-center" });
        } else {
          ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST, {position: "top-center"})
        }
        await safeClientLogEvent({
          ...USER_LOGS_CLIENT.ACCOUNT_PASSWORD_UPDATE_FAILED,
          uid: userId || null,
          source: "passwordBox.jsx",
          emailInfo: null,
          metadata: null,
          error: error.message || null
        });
      } finally {
        setLoading(false)
      }
    },
  });
  const oldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };
  const newPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };
  const reEnterPasswordVisibility = () => {
    setShowResetPassword(!showReEnterPassword);
  };
  return (
    <Box mx={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={passwordBoxSx.stackSx}
      >
        <TextLabel>Password</TextLabel>
        {!showEditPass ? (
          <Span
            direction="row"
            alignItems={"center"}
            onClick={() => handleOpen({ prop: "password" })}
          >
            <TextButton curser="pointer">Edit</TextButton>
            <KeyboardArrowDownIcon fontSize="small" color="primary" />
          </Span>
        ) : (
          <Span
            direction="row"
            alignItems={"center"}
            onClick={() => { handleClose(); passwordFormik.resetForm() } }
          >
            <TextButton curser="pointer">Edit</TextButton>
            <KeyboardArrowUpIcon fontSize="small" color="primary" />
          </Span>
        )}
      </Stack>
      {showEditPass && (
        <Box>
          <FormBox
            component="form"
            onSubmit={passwordFormik.handleSubmit}
            noValidate
          >
            <Grid container>
              <Grid lg={5} md={12} sm={12} xs={12}>
                <OldPassBox width="100%" margin="0 auto">
                  <StyledTextField
                    name="oldPassword"
                    label={
                      passwordFormik.touched.oldPassword &&
                        Boolean(passwordFormik.errors.oldPassword)
                        ? passwordFormik.errors.oldPassword
                        : "Old Password"
                    }
                    variant="standard"
                    type={showOldPassword ? "password" : "text"}
                    onChange={passwordFormik.handleChange}
                    value={passwordFormik.values.oldPassword}
                    error={
                      passwordFormik.touched.oldPassword &&
                      Boolean(passwordFormik.errors.oldPassword)
                    }
                    sx={passwordBoxSx.textFieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={oldPasswordVisibility}>
                            {showOldPassword ? (
                              <VisibilityOff fontSize="small"/>
                            ) : (
                              <Visibility fontSize="small"/>
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                </OldPassBox>
              </Grid>
              <Grid lg={7}></Grid>
              <Grid lg={5} md={12} sm={12} xs={12}>
                <Box sx={passwordBoxSx.textFieldBoxSx}>
                  <StyledTextField
                    name="newPassword"
                    label={
                      passwordFormik.touched.newPassword &&
                        Boolean(passwordFormik.errors.newPassword)
                        ? passwordFormik.errors.newPassword
                        : "New Password"
                    }
                    variant="standard"
                    type={showNewPassword ? "password" : "text"}
                    onChange={passwordFormik.handleChange}
                    value={passwordFormik.values.newPassword}
                    error={
                      passwordFormik.touched.newPassword &&
                      Boolean(passwordFormik.errors.newPassword)
                    }
                    sx={passwordBoxSx.textFieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={newPasswordVisibility}>
                            {showNewPassword ? (
                              <VisibilityOff fontSize="small"/>
                            ) : (
                              <Visibility fontSize="small"/>
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                </Box>
              </Grid>
              <Grid lg={2}></Grid>
              <Grid lg={5} md={12} sm={12} xs={12}>
                <Box sx={passwordBoxSx.textFieldBoxSx}>
                  <StyledTextField
                    name="reEnterPassword"
                    label={
                      passwordFormik.touched.reEnterPassword &&
                        Boolean(passwordFormik.errors.reEnterPassword)
                        ? passwordFormik.errors.reEnterPassword
                        : "Confirm New Password"
                    }
                    variant="standard"
                    type={showReEnterPassword ? "password" : "text"}
                    onChange={passwordFormik.handleChange}
                    value={passwordFormik.values.reEnterPassword}
                    error={
                      passwordFormik.touched.reEnterPassword &&
                      Boolean(passwordFormik.errors.reEnterPassword)
                    }
                    sx={passwordBoxSx.textFieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={reEnterPasswordVisibility}>
                            {showReEnterPassword ? (
                              <VisibilityOff fontSize="small"/>
                            ) : (
                              <Visibility fontSize="small"/>
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                </Box>
              </Grid>
            </Grid>
            <Grid container mt={3}>
              <Grid lg={2} md={2} sm={6} xs={6}>
                <Box
                  sx={passwordBoxSx.buttonBoxSx}
                >
                  <Button variant="contained" onClick={()=>{handleClose(); passwordFormik.resetForm()}}>
                    Cancel
                  </Button>
                </Box>
              </Grid>
              <Grid lg={2} md={2} sm={6} xs={6}>
                <Box
                  sx={passwordBoxSx.buttonBoxSx}
                >
                  <ButtonLoading text={"Update"} loading={loading} type={"submit"} />
                </Box>
              </Grid>
              <Grid lg={6} md={6} sm={6} xs={6}></Grid>
            </Grid>
          </FormBox>
        </Box>
      )}
      <hr />
    </Box>
  );
};
